import {
  genericGetSingle,
  genericCreateSingle,
  genericDeleteSingle,
  genericGetAll,
  genericUpdateSingle,
  makeAPIRequest,
} from "../helpers/apiCall";
import { Process } from "../models";
import { Error } from "../types";
import { getWorkflowStatusResponse } from "../types/processing";

/**
 * Get all processes
 * @param params URLSearchParams
 * @returns Promise<{ processes: Process[] }>
 */
function getAll(
  params: URLSearchParams = null,
): Promise<{ processes: Process[] }> {
  return genericGetAll("processes", params);
}

// For getting order details
/**
 * Get a single process
 * @param id number | string
 * @returns Promise<{ process: Process }>
 */
function getSingle(id: number | string) {
  return genericGetSingle("processes", id);
}

/**
 * Create a single process
 * @param item { [key: string]: string | number | boolean | Date | undefined}
 * @returns Promise<{ process: Process }>
 */
function createSingle(item: {
  [key: string]: string | number | boolean | Date | undefined | string[];
}): Promise<{ process: Process }> {
  return genericCreateSingle("processes", item);
}

/**
 *
 * @param id number | string
 * @param item { [key: string]: string | number | boolean | Date | undefined}
 * @returns Promise<{ process: Process } | Error>
 */
function updateSingle(
  id: number | string,
  item: {
    [key: string]:
      | string
      | number
      | boolean
      | Date
      | undefined
      | { quantity: number; serialNumber: number }[];
  },
): Promise<{ process: Process } | Error> {
  return genericUpdateSingle("processes", id, item);
}

/**
 * Delete a single process
 * @param id number | string
 * @returns Promise<{ deleted: boolean } | { error: string }>
 */
function deleteSingle(id: number | string): Promise<{ deleted: boolean }> {
  return genericDeleteSingle("processes", id);
}

/**
 * Gets the count of processes
 * @param params URLSearchParams
 * @returns Promise<{ count: number }>
 */
function getCount(params: URLSearchParams = null): Promise<{ count: number }> {
  params.append("countOnly", "true");
  return genericGetAll("processes", params);
}

/**
 * Starts the process for a given powerpick order.
 *
 * @param powerpickId - The ID of the powerpick order to queue.
 * @returns A promise resolving with the queue status, process ID, and message.
 */
function queueOrder(powerpickId: string): Promise<{
  queued: boolean;
  processId: string | null;
  message: string;
  workflowId: string | null;
}> {
  return makeAPIRequest({ url: `/orders/${powerpickId}/queue`, method: "get" })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Cancels a process with the given ID.
 * @param processId The string identifier of the process to cancel.
 * @returns A promise that resolves to an object containing a 'cancelled' flag and a status message.
 */
function cancelProcess(
  processId: string,
): Promise<{ cancelled: boolean; message: string }> {
  return makeAPIRequest({
    url: `/processes/${processId}/cancel`,
    method: "put",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Finishes a process by sending a PUT request to the API.
 *
 * @param processId string The ID of the process to finish.
 * @returns Promise<{ finished: boolean; message: string }> A promise resolving to an object containing the finished status and a message.
 */
function finishProcess(
  processId: string,
): Promise<{ finished: boolean; message: string }> {
  return makeAPIRequest({
    url: `/processes/${processId}/finish`,
    method: "put",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Retrieves the processing status for the specified process ID.
 *
 * @param processId The ID of the process to retrieve status for.
 * @returns A promise that resolves to an object containing the status and message.
 */
function getProcessingStatus(processId: string): Promise<{
  status?: string;
  message?: string;
  inputNeeded?: string;
  active?: boolean;
  locationName?: string;
  lot?: string;
  serialNumber?: string;
  value?: string | number;
  field?: string;
  materialName?: string;
  masterOrderLineId?: string;
  incompleteAllocations?: number;
  remainingTasks?: number;
}> {
  return makeAPIRequest({
    url: `/processes/${processId}/status`,
    method: "get",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Skips a specific task for a given process by making an API request to the skip endpoint.
 * @param {string} processId - The ID of the process whose task is to be skipped.
 * @returns {Promise<{ message: string }>} A promise that resolves with an object containing a message.
 */
function skipTask(processId: string): Promise<{ message: string }> {
  return makeAPIRequest({
    url: `/processes/${processId}/skip`,
    method: "put",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Confirms a task for a given process by sending a PUT request.
 * @param processId - The ID of the process to confirm.
 * @param quantity - The quantity to confirm in the task.
 * @returns A Promise that resolves to an object containing finished status and message.
 */
function confirmTask(
  processId: string,
  quantity: number,
): Promise<{ finished: boolean; message: string }> {
  return makeAPIRequest({
    url: `/processes/${processId}/confirm`,
    method: "put",
    body: { quantity },
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

/**
 * Retrieves the status of a workflow.
 *
 * @param workflowId The ID of the workflow to retrieve the status for.
 * @returns A promise that resolves to the workflow status response.
 */
function getWorkflowStatus(
  workflowId: string,
): Promise<getWorkflowStatusResponse> {
  return makeAPIRequest({
    url: `/workflows/${workflowId}/status`,
    method: "get",
  })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

export const processService = {
  getAll,
  getSingle,
  createSingle,
  updateSingle,
  deleteSingle,
  getCount,
  queueOrder,
  cancelProcess,
  finishProcess,
  getProcessingStatus,
  skipTask,
  confirmTask,
  getWorkflowStatus,
};
